import React, { useEffect, useState } from 'react'

import { fetchProgramAuthorizationHistory } from '../services/investmentProgramApi'
import AuthorizationProvenance from './AuthorizationProvenance'

const origins = {
  INITIAL_BOARD_AUTHORIZATION: 'Initial Board Authorization',
  PROGRAM_REAUTHORIZATION: 'Program Reauthorization',
  UNKNOWN: 'Authority origin unavailable',
}

const date = (value) => value && Number.isFinite(Date.parse(value)) ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(value)) : 'Unavailable'

const budget = (value) => {
  if (!value || value.amount == null || value.amount === '') return 'Unavailable'
  const amount = Number(value.amount)
  if (!Number.isFinite(amount)) return 'Unavailable'
  if (!value.currency) return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(amount)
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: value.currency, currencyDisplay: 'code' }).format(amount)
  } catch (_) {
    return `${value.currency} ${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(amount)}`
  }
}

export default function ProgramAuthorizationHistory({ programReference }) {
  const [history, setHistory] = useState(null)
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState(null)

  useEffect(() => {
    let active = true
    setHistory(null); setFailed(false)
    setExpanded(null)
    fetchProgramAuthorizationHistory({ programReference })
      .then((data) => active && setHistory(data))
      .catch(() => active && setFailed(true))
    return () => { active = false }
  }, [programReference])

  const versionsByReference = new Map((history || []).map((version) => [version.authorization_reference, version.version_number]))

  return <section aria-labelledby="authorization-history-title" className="rounded-2xl border bg-white p-5 shadow-sm sm:p-6">
    <h2 id="authorization-history-title" className="text-xl font-bold">Program Authorization History</h2>
    <p className="mt-1 text-sm text-slate-600">Immutable history of Board-authorized changes to the Program's authority.</p>
    {failed && <p role="alert" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Authorization history is currently unavailable. Other Program information remains available.</p>}
    {!failed && history === null && <p className="mt-4 text-sm text-slate-600">Loading authorization history…</p>}
    {!failed && history?.length === 0 && <p className="mt-4 rounded-xl border border-dashed p-4 text-sm text-slate-600">No authorization history is available.</p>}
    {history?.length > 0 && <ol aria-label="Program authority timeline" className="mt-4">
      {history.map((version, index) => {
        const previousVersion = versionsByReference.get(version.previous_authorization_reference)
        const open = expanded === version.authorization_reference
        const panelId = `historical-evidence-${version.authorization_reference}`
        return <li key={version.authorization_reference} className="relative pb-7 last:pb-0">
        {index < history.length - 1 && <span aria-hidden="true" className="absolute bottom-1 left-5 text-xl leading-none text-slate-400">↓</span>}
        <article aria-labelledby={`authorization-version-${version.authorization_reference}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id={`authorization-version-${version.authorization_reference}`} className="font-semibold">V{version.version_number} — {origins[version.authority_origin] || origins.UNKNOWN}</h3>
          {version.is_current && <span className="rounded-full border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-bold text-green-800">CURRENT</span>}
        </div>
        <dl className="mt-2 grid gap-x-5 gap-y-1.5 text-sm sm:grid-cols-2">
          <div><dt className="font-semibold text-slate-600">Effective</dt><dd>{date(version.effective_from)} → {version.is_current ? 'Current' : date(version.effective_to)}</dd></div>
          <div><dt className="font-semibold text-slate-600">Authorized Budget</dt><dd className="tabular-nums">{budget(version.authorized_budget)}</dd>{version.authorized_budget?.amount != null && !version.authorized_budget?.currency && <dd className="text-slate-600">Currency unavailable</dd>}</div>
          {version.authority_origin === 'PROGRAM_REAUTHORIZATION' && previousVersion != null && <div><dt className="font-medium text-slate-500">Previous Authority</dt><dd>V{previousVersion}</dd></div>}
          <div><dt className="font-medium text-slate-500">Authorization Conditions</dt><dd>{version.authorization_conditions_count == null ? 'Unavailable' : version.authorization_conditions_count === 0 ? 'No conditions' : version.authorization_conditions_count}</dd></div>
          <div className="sm:col-span-2"><dt className="font-medium text-slate-500">Board Decision</dt><dd className="break-all font-mono text-xs text-slate-700">{version.source_board_decision_reference || 'Reference unavailable'}</dd></div>
        </dl>
        <button type="button" className="mt-3 font-semibold text-blue-700 underline" aria-expanded={open} aria-controls={panelId} onClick={() => setExpanded(open ? null : version.authorization_reference)}>{open ? 'Hide historical evidence' : 'View historical evidence'}</button>
        <div id={panelId} hidden={!open}>{open && <AuthorizationProvenance provenance={version.provenance} />}</div>
        </article>
      </li>})}
    </ol>}
  </section>
}
